/**
 * Combined precondition check for publishing a Scene as a standalone `.gltf`
 * document (no folder to resolve a relative uri against).
 *
 * Runs the flat-scene rule and the src-ref-only rule in a single pass and
 * formats the offenders into one actionable error message. Callers (the
 * sandbox CMS publish path, the exporter itself) get a single throw/return
 * instead of a sequence of partial failures.
 *
 * Expected input shape: the output of `TraceScene.getFullExportData()` — the
 * implicit scene root prepended, descendants with absolute parent paths.
 */

import type { SceneObjectData } from '../scenedata/sceneobjectdata';
import { validateFlatScene, formatFlatSceneOffenders, type FlatSceneOffender } from './validateFlatScene';
import { validateSrcRefAssets, formatSrcRefOffenders, type SrcRefOffender } from './validateSrcRefAssets';

export interface PublishableSceneValidationResult {
    ok: boolean;
    flatOffenders: FlatSceneOffender[];
    srcRefOffenders: SrcRefOffender[];
    /** Combined human-readable error message (empty when `ok`). */
    message: string;
}

export function validatePublishableScene(sceneData: SceneObjectData[]): PublishableSceneValidationResult {
    const flat = validateFlatScene(sceneData);
    const srcRef = validateSrcRefAssets(sceneData);
    const ok = flat.ok && srcRef.ok;

    const sections: string[] = [];
    if (!flat.ok) {
        sections.push(
            `Scene must be flat — nested hierarchy isn't allowed in published Scenes:\n${formatFlatSceneOffenders(flat.offenders)}`,
        );
    }
    if (!srcRef.ok) {
        sections.push(
            `Scene contains assets that aren't published as URLs — upload them as Assets first, then drag them in:\n${formatSrcRefOffenders(srcRef.offenders)}`,
        );
    }

    return {
        ok,
        flatOffenders: flat.offenders,
        srcRefOffenders: srcRef.offenders,
        message: sections.join('\n\n'),
    };
}
